import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  createOrder,
  fetchCustomers,
  fetchDeliveries,
} from "../../../../../../../services/pos/Tables";

const CreateOrderForm = ({  rootId, onSuccess }) => {
  const [formData, setFormData] = useState({
    order_type: "delivery",
    customer_id: "",
    customer_name: "",
    email: "",
    customer_phones: ["", "", ""], // ثلاث خانات للهواتف
    customer_addresses: ["", "", ""], // ثلاث خانات للعناوين
    delivery_id: "",
      captain_order_id: "", // ✅ ضفناها هنا بدل ما ننادي formData قبل تعريفه

    
  });

  const [visiblePhones, setVisiblePhones] = useState(1); // عدد الحقول المرئية للهواتف
  const [visibleAddresses, setVisibleAddresses] = useState(1); // عدد الحقول المرئية للعناوين
  const [customers, setCustomers] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const formRef = useRef(null);

  // قائمة خيارات الأرقام المأخوذة من العملاء
  const phoneOptions = customers.flatMap((c) =>
    ["phone1", "phone2", "phone3"]
      .map((k) => ({ phone: c[k], customer: c }))
      .filter((o) => o.phone)
  );

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await fetchCustomers();
        setCustomers(data || []);
      } catch (err) {
        console.error("فشل تحميل العملاء", err);
      }
    };
    loadCustomers();
  }, []);

  useEffect(() => {
    const loadDeliveries = async () => {
      try {
        const data = await fetchDeliveries();
        setDeliveries(data || []); // جلب أسماء الدليفري
      } catch (err) {
        console.error("فشل تحميل الدليفري", err);
      }
    };
    loadDeliveries();
  }, []);

  // ✅ useEffect لإضافة اختصار Enter لتنفيذ الإرسال
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من مفتاح Enter فقط
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        // تجنب التنفيذ إذا كنا داخل TextField متعدد الأسطر
        if (e.target.tagName !== 'TEXTAREA') {
          e.preventDefault(); // منع السلوك الافتراضي
          if (formRef.current) {
            formRef.current.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true })
            );
          }
        }
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (idx, value) => {
    const phones = [...formData.customer_phones];
    phones[idx] = value;
    setFormData((prev) => ({ ...prev, customer_phones: phones }));
  };

  const handleAddPhone = () => {
    setVisiblePhones((prev) => Math.min(prev + 1, 3)); // فتح الحقل التالي حتى 3 فقط
  };

  const handleAddressChange = (idx, value) => {
    const addresses = [...formData.customer_addresses];
    addresses[idx] = value;
    setFormData((prev) => ({ ...prev, customer_addresses: addresses }));
  };

  const handleAddAddress = () => {
    setVisibleAddresses((prev) => Math.min(prev + 1, 3)); // فتح الحقل التالي حتى 3 فقط
  };

  const handleRemoveAddress = (idx) => {
    setFormData((prev) => ({
      ...prev,
      customer_addresses: prev.customer_addresses.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const phones = formData.customer_phones;
      const addresses = formData.customer_addresses;
      const payload = {
        ...formData,
        customer_name:
          formData.customer_name && formData.customer_name.trim() !== ""
            ? formData.customer_name
            : "DeliveryCustomer", // إذا لم يُكتب اسم عميل
        customer_phone1: phones[0] || "",
        customer_phone2: phones[1] || "",
        customer_phone3: phones[2] || "",
        customer_address1: addresses[0] || "",
        customer_address2: addresses[1] || "",
        customer_address3: addresses[2] || "",
        order_type: "delivery",
        delivery_id: formData.delivery_id || "",
              captain_order_id: formData.captain_order_id || "", // 🟢 أضفنا ده


      };
      delete payload.customer_phones;
      delete payload.customer_addresses;
      delete payload.table_id;

      const res = await createOrder(payload);
      setResult({ success: true, data: res });

      if (onSuccess && res?.data?.order_id) {
        onSuccess({ id: res.data.order_id, status: res.data.sttus });
      }
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{ p: 3, maxWidth: 700, mx: "auto", mt: 4, borderRadius: 3 }}
    >
      <Typography variant="h5" fontWeight="bold" mb={3} align="center">
        إنشاء طلب جديد
      </Typography>
      <form ref={formRef} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={customers.filter(
                (c) =>
                  c.name?.trim().toLowerCase() !== "tablescustomer" &&
                  c.name?.trim().toLowerCase() !== "takeawaycustomer"
              )}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.name || `عميل (${option.id})`
              }
              value={
                customers.find(
                  (c) => c.id.toString() === formData.customer_id
                ) ||
                (formData.customer_name
                  ? { name: formData.customer_name }
                  : null)
              }
              onChange={(e, newValue) => {
                if (typeof newValue === "string") {
                  setFormData({
                    ...formData,
                    customer_id: "",
                    customer_name: newValue,
                  });
                } else if (newValue && newValue.id) {
                  setFormData({
                    ...formData,
                    customer_id: newValue.id.toString(),
                    customer_name: newValue.name,
                    email: newValue.email || "",
                    customer_phones: [newValue.phone1 || ""],
                    customer_addresses: [newValue.address1 || ""],
                  });
                }
              }}
              onInputChange={(e, value) =>
                setFormData((prev) => ({ ...prev, customer_name: value }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="العميل"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* الهواتف */}
          {formData.customer_phones
            .slice(0, visiblePhones)
            .map((phone, idx) => (
              <Grid item xs={6} key={`phone-${idx}`}>
                <Autocomplete
                  freeSolo
                  options={phoneOptions}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : opt.phone || ""
                  }
                  value={formData.customer_phones[idx] || ""}
                  onChange={(e, newValue) => {
                    const phoneVal =
                      typeof newValue === "string"
                        ? newValue
                        : newValue?.phone || "";
                    handlePhoneChange(idx, phoneVal);
                    if (newValue && newValue.customer) {
                      const c = newValue.customer;
                      setFormData((prev) => ({
                        ...prev,
                        customer_id: c.id ? c.id.toString() : "",
                        customer_name: c.name || prev.customer_name,
                        email: c.email || prev.email,
                        customer_phones: (() => {
                          const arr = [...prev.customer_phones];
                          arr[idx] = phoneVal;
                          return arr;
                        })(),
                        customer_addresses: [c.address1 || prev.customer_addresses[0], prev.customer_addresses[1], prev.customer_addresses[2]],
                      }));
                    }
                  }}
                  onInputChange={(e, value) => handlePhoneChange(idx, value)}
                  renderInput={(params) => (
                    <TextField {...params} label={`هاتف ${idx + 1}`} fullWidth />
                  )}
                />
              </Grid>
            ))}
          <Grid item xs={6}>
            <Button
              onClick={handleAddPhone}
              startIcon={<AddCircleOutlineIcon />}
              size="small"
            >
              إضافة رقم جديد
            </Button>
          </Grid>

          {/* العناوين */}
          {formData.customer_addresses
            .slice(0, visibleAddresses)
            .map((address, idx) => (
              <Grid item xs={6} key={`address-${idx}`}>
                <TextField
                  label={`عنوان ${idx + 1}`}
                  value={address}
                  onChange={(e) => handleAddressChange(idx, e.target.value)}
                  fullWidth
                />
              </Grid>
            ))}
          <Grid item xs={6}>
            <Button
              onClick={handleAddAddress}
              startIcon={<AddCircleOutlineIcon />}
              size="small"
            >
              إضافة عنوان جديد
            </Button>
          </Grid>

          {/* الدليفري */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="delivery-label">الدليفري</InputLabel>
              <Select
                labelId="delivery-label"
                name="delivery_id"
                value={formData.delivery_id}
                onChange={handleChange}
                label="الدليفري"
                MenuProps={{
                  disablePortal: true, // ✨ يرندر القائمة برا الديالوج
                  PaperProps: {
                    sx: {
                      zIndex: 2000, // أعلى من الديالوج (الـ Dialog = 1300, Modal = 1400, Popover = 1500)
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>اختر</em>
                </MenuItem>
                {deliveries.map((delivery) => (
                  <MenuItem key={delivery.id} value={delivery.id}>
                    {delivery.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
             <Button
               type="submit"
               variant="contained"
               disabled={loading}
               title="إرسال (Enter)" // ✅ تحديث tooltip
               endIcon={loading && <CircularProgress size={20} />}
             >
               {loading ? "جارٍ الإرسال..." : "إرسال"}
             </Button>
          </Grid>
        </Grid>
      </form>

      {result && (
        <Box mt={3}>
          {result.success ? (
            <Alert severity="success">✅ تم إنشاء الطلب بنجاح</Alert>
          ) : (
            <Alert severity="error">❌ {result.message}</Alert>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CreateOrderForm;